// useRoom — hook que encapsula o ArenaWS e reduz as mensagens do protocolo
// em estado React para os três apps (player, stage, host) — docs/02 §5.
import { useEffect, useMemo, useRef, useState } from "react";
import { ArenaWS, type ConnStatus } from "./ws";
import type {
  AnswerAckData,
  AnswerCountData,
  ClientType,
  LeaderboardData,
  Phase,
  PhaseData,
  PodiumData,
  Player,
  QuestionView,
  ReactionData,
  RevealData,
  ReviewListData,
  Role,
  RoomSettingsView,
  StateData,
} from "./protocol";

export interface RoomState {
  status: ConnStatus;
  phase: Phase | null;
  questionIndex: number;
  questionCount: number;
  deadline?: number;
  paused: boolean;
  locked: boolean;
  question?: QuestionView;
  // player
  you?: StateData["you"];
  players?: Player[];
  // host/stage
  answered: number;
  total: number;
  review?: ReviewListData["items"];
  reviewDeadline?: number;
  judgeUnavailable?: boolean;
  // resultados
  reveal?: RevealData;
  leaderboard?: LeaderboardData;
  podium?: PodiumData;
  answerAck?: AnswerAckData;
  reaction?: ReactionData;
  settings?: RoomSettingsView;
  kicked?: { reason: string };
  lastError?: { code: string; message: string };
  // clock
  serverNow: () => number;
}

export function useRoom(role: Role, code: string, token?: string, enabled = true) {
  const [status, setStatus] = useState<ConnStatus>("closed");
  const [state, setState] = useState<Partial<RoomState>>({});
  const wsRef = useRef<ArenaWS | null>(null);

  useEffect(() => {
    if (!enabled || !code) return;
    const ws = new ArenaWS({
      role,
      code,
      token,
      onStatus: (st) => {
        setStatus(st);
        setState((s) => ({ ...s, status: st }));
      },
      onMessage: (t, d) => {
        setState((s) => reduce(s, t, d));
      },
    });
    wsRef.current = ws;
    ws.connect();
    return () => {
      ws.close();
      wsRef.current = null;
    };
    // token muda só em novo join — recria a conexão
  }, [role, code, token, enabled]);

  const send = useMemo(() => (t: ClientType, d?: unknown) => wsRef.current?.send(t, d ?? {}), []);

  const out: RoomState = useMemo(
    () => ({
      status,
      phase: null,
      questionIndex: -1,
      questionCount: 0,
      paused: false,
      locked: false,
      answered: 0,
      total: 0,
      serverNow: () => wsRef.current?.serverNow() ?? Date.now(),
      ...state,
    }),
    [status, state],
  );

  return { ...out, send, ws: wsRef };
}

// reduce aplica cada ServerType ao estado acumulado.
function reduce(s: Partial<RoomState>, t: string, d: unknown): Partial<RoomState> {
  const data = (d ?? {}) as Record<string, unknown>;
  switch (t) {
    case "state": {
      const st = data as unknown as StateData;
      return {
        ...s,
        phase: st.phase,
        questionIndex: st.questionIndex ?? -1,
        questionCount: st.totalQuestions ?? 0,
        deadline: st.deadline,
        paused: !!st.paused,
        locked: !!st.locked,
        question: st.question,
        you: st.you,
        players: st.players as RoomState["players"],
        answered: st.answered ?? 0,
        total: st.players?.length ?? 0,
        review: st.review?.items,
        reviewDeadline: st.review?.deadline,
        judgeUnavailable: st.review?.judgeUnavailable,
        settings: st.settings,
        reveal: undefined,
        leaderboard: undefined,
        podium: undefined,
      };
    }
    case "phase": {
      const p = data as unknown as PhaseData;
      return {
        ...s,
        phase: p.phase,
        questionIndex: p.questionIndex ?? s.questionIndex,
        deadline: p.deadline,
        paused: !!p.paused,
        question: p.question ?? s.question,
        reveal: p.phase === "answering" ? undefined : s.reveal,
        leaderboard: undefined,
        podium: undefined,
        answerAck: p.phase === "answering" ? undefined : s.answerAck,
      };
    }
    case "answer.ack":
      return { ...s, answerAck: data as unknown as AnswerAckData };
    case "answer.count": {
      const c = data as unknown as AnswerCountData;
      return { ...s, answered: c.answered, total: c.total };
    }
    case "player.joined": {
      const p = (data as { player?: NonNullable<RoomState["players"]>[number] }).player;
      if (!p) return s;
      const cur = s.players ?? [];
      if (cur.some((x) => x.id === p.id)) return s;
      return { ...s, players: [...cur, p] };
    }
    case "player.left":
    case "player.kicked": {
      const p = (data as { player?: { id: string } }).player;
      if (!p) return s;
      return { ...s, players: (s.players ?? []).filter((x) => x.id !== p.id) };
    }
    case "review.list": {
      const rl = data as unknown as ReviewListData;
      return {
        ...s,
        review: rl.items,
        reviewDeadline: rl.deadline,
        deadline: rl.deadline ?? s.deadline,
        judgeUnavailable: rl.judgeUnavailable,
      };
    }
    case "reveal":
      return {
        ...s,
        reveal: data as unknown as RevealData,
        review: undefined,
        judgeUnavailable: undefined,
      };
    case "leaderboard":
      return { ...s, leaderboard: data as unknown as LeaderboardData };
    case "podium":
      return { ...s, podium: data as unknown as PodiumData };
    case "reaction":
      return { ...s, reaction: data as unknown as ReactionData };
    case "kicked":
      return { ...s, kicked: data as { reason: string } };
    case "error":
      return { ...s, lastError: data as { code: string; message: string } };
    default:
      return s;
  }
}
